import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Row, Col, Card, Form, Input, Select, InputNumber, Switch,
  Button, Avatar, Tag, Typography, Breadcrumb, Divider,
  Spin, Empty, Space, Segmented, Tooltip, Table, Modal, message,
} from 'antd';
import {
  HomeOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined,
  UserOutlined, PhoneOutlined, BankOutlined, SaveOutlined,
  HeartOutlined, MedicineBoxOutlined, SmileOutlined,
  ArrowLeftOutlined, FilePdfOutlined, EditOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventApi }        from '../../../api/event.api';
import { registrationApi } from '../../../api/registration.api';
import { checkupApi }      from '../../../api/checkup.api';
import { smilepassportApi } from '../../../api/smilepassport.api';
import SmilepassportModal from '../../event/components/SmilepassportModal';
import SmilepassportPreviewModal from '../../event/components/SmilepassportPreviewModal';
import useDebounce         from '../../../hooks/useDebounce';
import { usePermission }   from '../../../hooks/usePermission';
import { ORAL_HYGIENE, JAW_DEVELOPMENT, JAW_WIDTH, TONGUE_MOVEMENT, MOUTH_BREATHING } from '../../../utils/constants';

const { Title, Text } = Typography;

const calcAge = (dob) => dob ? dayjs().diff(dayjs(dob), 'year') : null;

// ── Nav tab config ────────────────────────────────────────────────
const NAV_TABS = [
  { key: 'checkup',   label: 'Checkup',       icon: <HeartOutlined /> },
  { key: 'treatment', label: 'Treatment',      icon: <MedicineBoxOutlined /> },
  { key: 'passport',  label: 'Smile Passport', icon: <SmileOutlined /> },
];

// ── Child list item ───────────────────────────────────────────────
const ChildListItem = ({ reg, active, hasCheckup, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
      background: active ? 'var(--wl-primary-pale)' : 'transparent',
      border: active ? '1.5px solid var(--wl-primary-light)' : '1.5px solid transparent',
      transition: 'all 0.15s', marginBottom: 4,
    }}
  >
    <Avatar
      size={34}
      style={{
        flexShrink: 0,
        background: active ? 'var(--wl-primary)' : 'var(--wl-primary-pale)',
        color: active ? '#fff' : 'var(--wl-primary)',
        fontWeight: 700, fontSize: 14,
      }}
    >
      {reg.child?.name?.charAt(0)?.toUpperCase()}
    </Avatar>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Text strong ellipsis style={{ fontSize: 13, color: active ? 'var(--wl-primary)' : 'var(--wl-text)', display: 'block' }}>
        {reg.child?.name}
      </Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {reg.child?.parent?.phone || '—'}
      </Text>
    </div>
    <Tooltip title={hasCheckup ? 'Checked' : 'Pending'}>
      {hasCheckup
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 15 }} />
        : <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 15 }} />}
    </Tooltip>
  </div>
);

// ── "Complete checkup first" placeholder ─────────────────────────
const RequireCheckupFirst = () => (
  <div style={{ textAlign: 'center', padding: '40px 0' }}>
    <HeartOutlined style={{ fontSize: 36, color: 'var(--wl-primary-pale)', marginBottom: 12 }} />
    <div style={{ fontSize: 14, fontWeight: 600, color: '#888', marginBottom: 4 }}>
      Complete Checkup First
    </div>
    <div style={{ fontSize: 12, color: '#aaa' }}>
      Save the Checkup tab before filling this section.
    </div>
  </div>
);

const SmilePassportTab = ({
  child, checkup, isDoctor,
  onIssue, onUpdatePassport, onRevoke,
  issueLoading, revokeLoading,
}) => {
  const [passportForm] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!formVisible) {
      passportForm.resetFields();
      return;
    }
    if (editMode && checkup) {
      passportForm.setFieldsValue({ passportNotes: checkup.passportNotes });
    }
  }, [formVisible, editMode, checkup, passportForm]);

  const handleSubmit = async () => {
    const values = await passportForm.validateFields().catch(() => null);
    if (!values) return;
    const handler = editMode ? onUpdatePassport : onIssue;
    handler(values, () => setFormVisible(false));
  };

  if (!checkup) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <SmileOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
        <div style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>No checkup recorded yet</div>
        <div style={{ color: 'var(--wl-text-muted)', fontSize: 12 }}>Record a checkup first before issuing a smile passport</div>
      </div>
    );
  }

  const hasPassport = !!checkup.smilePassportIssued;

  if (formVisible) {
    return (
      <Card
        title={
          <Space>
            <SmileOutlined style={{ color: 'var(--wl-primary-light)' }} />
            <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>
              {editMode ? 'Update Passport' : 'Issue Smile Passport'}
            </span>
          </Space>
        }
        extra={<Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setFormVisible(false)}>Back to Details</Button>}
        style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
      >
        <Form form={passportForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Issued To">
                <Input value={child?.name} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Issue Date">
                <Input value={dayjs().format('DD MMM YYYY')} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="passportNotes" label="Notes">
            <Input.TextArea rows={3} placeholder="Any notes about the passport…" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={issueLoading}
              onClick={handleSubmit}
              style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', borderRadius: 9999 }}
            >
              {editMode ? 'Update' : 'Issue Passport'}
            </Button>
            <Button onClick={() => setFormVisible(false)}>Cancel</Button>
          </div>
        </Form>
      </Card>
    );
  }
};

// ── Checkup Modal Component ────────────────────────────────────────
const CheckupModal = ({
  open, onClose, eventId, childId, childName,
  editingRecord, onSuccess,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (editingRecord) {
      form.setFieldsValue({
        oralHygieneStatus: editingRecord.oralHygieneStatus,
        weight: editingRecord.weight,
        height: editingRecord.height,
        hasCavities: editingRecord.hasCavities ?? false,
        hasGumIssues: editingRecord.hasGumIssues ?? false,
        painReported: editingRecord.painReported ?? false,
        treatmentRequired: editingRecord.treatmentRequired ?? false,
        jawDevelopment: editingRecord.jawDevelopment,
        upperJaw: editingRecord.upperJaw,
        lowerArch: editingRecord.lowerArch,
        tongueMovement: editingRecord.tongueMovement,
        mouthBreathing: editingRecord.mouthBreathing ?? false,
        treatmentType: editingRecord.treatmentType,
        nextVisitMonths: editingRecord.nextVisitMonths,
        treatmentNotes: editingRecord.treatmentNotes,
        doctorRemarks: editingRecord.doctorRemarks,
      });
    }
  }, [open, editingRecord, form]);

  const createMutation = useMutation({
    mutationFn: (data) => checkupApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['event-checkups', eventId]);
      message.success('Checkup recorded');
      onSuccess?.();
      onClose();
    },
    onError: (err) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => checkupApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['event-checkups', eventId]);
      message.success('Checkup updated');
      onSuccess?.();
      onClose();
    },
    onError: (err) => message.error(err.message),
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }

    const payload = { ...values, event: eventId, child: childId };
    if (editingRecord) {
      updateMutation.mutate({ id: editingRecord.id || editingRecord._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.45)',
        display: open ? 'flex' : 'none',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <Card
        style={{
          borderRadius: 14, width: '90%', maxWidth: 700,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        title={
          <Space>
            <HeartOutlined style={{ color: 'var(--wl-primary-light)' }} />
            <span>{editingRecord ? 'Edit Checkup' : 'Record Checkup'}</span>
          </Space>
        }
        extra={<Button type="text" onClick={onClose}>✕</Button>}
        onClick={(e) => e.stopPropagation()}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Child">
                <Input value={childName} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Record Date">
                <Input value={dayjs().format('DD MMM YYYY')} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '8px 0 16px' }}>Health Metrics</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="oralHygieneStatus"
                label="Oral Hygiene Status"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Select status">
                  {Object.entries(ORAL_HYGIENE).map(([k, v]) => (
                    <Select.Option key={v} value={v}>
                      {k.charAt(0) + k.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="weight" label="Weight (kg)">
                <InputNumber style={{ width: '100%' }} min={1} max={200} placeholder="kg" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="height" label="Height (cm)">
                <InputNumber style={{ width: '100%' }} min={50} max={250} placeholder="cm" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {[
              { name: 'hasCavities', label: 'Cavities' },
              { name: 'hasGumIssues', label: 'Gum Issues' },
              { name: 'painReported', label: 'Pain Reported' },
              { name: 'treatmentRequired', label: 'Needs Treatment' },
            ].map(({ name, label }) => (
              <Col xs={12} sm={6} key={name}>
                <Form.Item name={name} label={label} valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Divider style={{ margin: '8px 0 16px' }}>Dental Assessment</Divider>

          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="jawDevelopment" label="Jaw Development">
                <Select placeholder="Select" allowClear>
                  <Select.Option value="good">Good</Select.Option>
                  <Select.Option value="to_be_assigned">To Be Assisted</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="upperJaw" label="Upper Jaw">
                <Select placeholder="Select" allowClear>
                  <Select.Option value="wide">Wide</Select.Option>
                  <Select.Option value="narrow">Narrow</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="lowerArch" label="Lower Arch">
                <Select placeholder="Select" allowClear>
                  <Select.Option value="wide">Wide</Select.Option>
                  <Select.Option value="narrow">Narrow</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="tongueMovement" label="Tongue Movement">
                <Select placeholder="Select" allowClear>
                  <Select.Option value="good">Good</Select.Option>
                  <Select.Option value="dysfunction">Dysfunction</Select.Option>
                  <Select.Option value="restricted">Restricted</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="mouthBreathing" label="Mouth Breathing" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '8px 0 16px' }}>Treatment Details</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="treatmentType" label="Treatment Type">
                <Input placeholder="e.g. Filling, Extraction, Scaling" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="nextVisitMonths" label="Next Visit (months)">
                <InputNumber style={{ width: '100%' }} min={1} max={24} placeholder="months" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="treatmentNotes" label="Treatment Notes">
                <Input.TextArea rows={3} placeholder="Treatment notes…" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="doctorRemarks" label="Doctor Remarks">
                <Input.TextArea rows={3} placeholder="Observations, remarks…" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isLoading}
              onClick={handleSubmit}
              style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
            >
              {editingRecord ? 'Save Changes' : 'Record Checkup'}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────
const CheckupPage = () => {
  const { eventId, registrationId } = useParams();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const { isDoctor } = usePermission();

  const [childSearch, setChildSearch] = useState('');
  const [filterTab,   setFilterTab]   = useState('all');    // 'all' | 'pending' | 'checked'
  const [activeTab,   setActiveTab]   = useState('checkup'); // 'checkup' | 'treatment' | 'passport'
  const [checkupModalOpen, setCheckupModalOpen] = useState(false);
  const [checkupEditingRecord, setCheckupEditingRecord] = useState(null);
  const [passportModalOpen, setPassportModalOpen] = useState(false);
  const [passportEditingRecord, setPassportEditingRecord] = useState(null);
  const [passportPreviewOpen, setPassportPreviewOpen] = useState(false);
  const [passportPreviewRecord, setPassportPreviewRecord] = useState(null);

  const debouncedSearch = useDebounce(childSearch, 250);

  // Reset to checkup tab when navigating to a new child
  useEffect(() => { setActiveTab('checkup'); }, [registrationId]);

  // ── Queries ───────────────────────────────────────────────────
  const { data: eventData,  isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn:  () => eventApi.getById(eventId),
  });

  const { data: regData, isLoading: regLoading } = useQuery({
    queryKey: ['event-registrations', eventId],
    queryFn:  () => registrationApi.getByEvent(eventId),
  });

  const { data: checkupsData } = useQuery({
    queryKey: ['event-checkups', eventId],
    queryFn:  () => checkupApi.getAll({ event: eventId, limit: 500 }),
  });

  // ── Derived ───────────────────────────────────────────────────
  const allRegistrations = regData?.data || [];

  const checkupByChildId = useMemo(() => {
    const map = {};
    (checkupsData?.data || []).forEach((c) => {
      const cid = c.child?.id || c.child?._id || String(c.child || '');
      if (cid) map[cid] = c;
    });
    return map;
  }, [checkupsData]);

  const checkedCount = useMemo(
    () => allRegistrations.filter(r => !!checkupByChildId[String(r.child?.id || r.child?._id || '')]).length,
    [allRegistrations, checkupByChildId]
  );
  const pendingCount = allRegistrations.length - checkedCount;

  const filteredRegs = useMemo(() => {
    let rows = allRegistrations;
    if (filterTab === 'pending') rows = rows.filter(r => !checkupByChildId[String(r.child?.id || r.child?._id || '')]);
    if (filterTab === 'checked') rows = rows.filter(r => !!checkupByChildId[String(r.child?.id || r.child?._id || '')]);
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter(r =>
        r.child?.name?.toLowerCase().includes(q) ||
        String(r.child?.parent?.phone ?? '').includes(q)
      );
    }
    return [...rows].sort((a, b) => (a.child?.name || '').localeCompare(b.child?.name || ''));
  }, [allRegistrations, debouncedSearch, filterTab, checkupByChildId]);

  const currentReg      = allRegistrations.find(r => (r.id || r._id) === registrationId);
  const child           = currentReg?.child;
  const childId         = child?.id || child?._id;
  const existingCheckup = childId ? checkupByChildId[String(childId)] : null;
  const isEdit          = !!existingCheckup;

  const { data: passportRecords, isLoading: passportLoading } = useQuery({
    queryKey: ['event-smilepassports', eventId, childId],
    queryFn:  () => childId ? smilepassportApi.getByEventAndChild(eventId, childId) : Promise.resolve([]),
    enabled: !!childId,
    select:  (data) => data?.data || [],
  });

  // ── Mutations ─────────────────────────────────────────────────
  const invalidate = () => {
    queryClient.invalidateQueries(['event-registrations', eventId]);
    queryClient.invalidateQueries(['event-checkups',      eventId]);
    queryClient.invalidateQueries(['event-checkup-stats', eventId]);
    queryClient.invalidateQueries(['event',               eventId]);
  };

  const issuePassportMutation = useMutation({
    mutationFn: (data) => checkupApi.update(
      existingCheckup?.id || existingCheckup?._id,
      { smilePassportIssued: true, ...data }
    ),
    onSuccess: () => { invalidate(); message.success('Smile passport Published'); },
    onError:   (err) => message.error(err.message),
  });

  // ── Submit handlers ───────────────────────────────────────────

  // ── Loading ───────────────────────────────────────────────────
  if (eventLoading || regLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/events')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Events</span> },
          { title: <span onClick={() => navigate(`/events/${eventId}`)} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>{event?.title}</span> },
          { title: 'Checkup' },
        ]}
      />
      <Row gutter={[20, 20]}>
      {/* ── Right: child + tabs ───────────────────────────────── */}
      <Col xs={24} lg={24}>
        {!currentReg ? (
          <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
            <Empty
              description="Select a child from the list to begin checkup"
              style={{ padding: 60 }}
            />
          </Card>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>

            {/* Child summary */}
            <Card
              style={{
                borderRadius: 14, border: '1.5px solid var(--wl-primary-pale)',
                background: 'linear-gradient(135deg, var(--wl-primary-pale) 0%, var(--wl-card-bg) 100%)',
              }}
              styles={{ body: { padding: '14px 20px' } }}
            >
              <Row gutter={[12, 8]} align="middle">
                <Col flex="56px" style={{ textAlign: 'center' }}>
                  <Avatar
                    size={48}
                    style={{
                      background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))',
                      fontSize: 20, fontWeight: 700,
                      fontFamily: "'Fredoka One', sans-serif",
                    }}
                  >
                    {child?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </Col>
                <Col flex="auto">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Title level={5} style={{ margin: 0, color: 'var(--wl-text)' }}>{child?.name}</Title>
                    {child?.gender && (
                      <Tag style={{ borderRadius: 9999, textTransform: 'capitalize', fontSize: 11, margin: 0 }}>
                        {child.gender}
                      </Tag>
                    )}
                    {(child?.dateOfBirth || child?.age != null) && (
                      <Tag style={{ borderRadius: 9999, fontSize: 11, margin: 0, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>
                        {child?.age != null ? `${child.age} yrs` : `${calcAge(child.dateOfBirth)} yrs`}
                      </Tag>
                    )}
                    <Tag
                      icon={isEdit ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                      color={isEdit ? 'success' : 'warning'}
                      style={{ borderRadius: 9999, fontSize: 11, margin: 0 }}
                    >
                      {isEdit ? 'Checked' : 'Pending'}
                    </Tag>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 5, flexWrap: 'wrap' }}>
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <UserOutlined style={{ color: 'var(--wl-primary)', marginRight: 4 }} />
                      {child?.parent?.name || '—'}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <PhoneOutlined style={{ color: 'var(--wl-primary)', marginRight: 4 }} />
                      {child?.parent?.phone || '—'}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <BankOutlined style={{ color: 'var(--wl-primary)', marginRight: 4 }} />
                      {child?.school?.name || '—'}
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Tab card */}
            <Card
              style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
              styles={{ body: { padding: 0 } }}
            >
              {/* Custom tab bar */}
              <div style={{ padding: '14px 16px 0' }}>
                <div style={{
                  display: 'flex', gap: 3,
                  background: 'var(--wl-primary-pale)',
                  borderRadius: 12, padding: 4,
                }}>
                  {NAV_TABS.map((tab) => {
                    const isActive  = activeTab === tab.key;
                    const isDimmed  = !isEdit && tab.key !== 'checkup';
                    return (
                      <div
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                          flex: 1, textAlign: 'center',
                          padding: '9px 8px',
                          borderRadius: 9,
                          cursor: 'pointer',
                          background: isActive
                            ? 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))'
                            : 'transparent',
                          color: isActive ? '#fff' : isDimmed ? 'var(--wl-primary-light)' : 'var(--wl-primary)',
                          fontWeight: 600,
                          fontSize: 13,
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          userSelect: 'none',
                        }}
                      >
                        {tab.icon}
                        <span style={{ whiteSpace: 'nowrap' }}>{tab.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tab content */}
              <div style={{ padding: 20 }}>

                {/* ── Checkup tab ── */}
                {activeTab === 'checkup' && (
                  <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }} bodyStyle={{ padding: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16 }}>
                      <div>
                        <Text strong style={{ fontSize: 15 }}>Checkup Records</Text>
                        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                          Record and manage checkup details for this child.
                        </Text>
                      </div>
                      {isDoctor && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => { setCheckupEditingRecord(null); setCheckupModalOpen(true); }}
                          disabled={!childId}
                          style={{ borderRadius: 9999, background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
                        >
                          {existingCheckup ? 'Edit Checkup' : 'Record Checkup'}
                        </Button>
                      )}
                    </div>
                    <Table
                      dataSource={existingCheckup ? [existingCheckup] : []}
                      columns={[
                        { title: 'Oral Hygiene', dataIndex: 'oralHygieneStatus', width: 130, render: (v) => <Tag style={{ textTransform: 'capitalize' }}>{v}</Tag> },
                        { title: 'Weight (kg)', dataIndex: 'weight', width: 100, align: 'center' },
                        { title: 'Height (cm)', dataIndex: 'height', width: 100, align: 'center' },
                        {
                          title: 'Conditions',
                          width: 140,
                          render: (_, r) => {
                            const conditions = [];
                            if (r.hasCavities) conditions.push('Cavities');
                            if (r.hasGumIssues) conditions.push('Gum Issues');
                            if (r.painReported) conditions.push('Pain');
                            return conditions.length > 0 ? (
                              <Space size={4} wrap>
                                {conditions.map(c => <Tag key={c} color="volcano" style={{ fontSize: 11 }}>{c}</Tag>)}
                              </Space>
                            ) : '—';
                          },
                        },
                        {
                          title: 'Treatment',
                          width: 100,
                          render: (_, r) => (
                            <Tag color={r.treatmentRequired ? 'orange' : 'default'}>
                              {r.treatmentRequired ? 'Yes' : 'No'}
                            </Tag>
                          ),
                        },
                        { title: 'Type', dataIndex: 'treatmentType', width: 120 },
                        { title: 'Next Visit', width: 120, render: (_, r) => r.nextVisitMonths ? `${r.nextVisitMonths}mo` : '—' },
                        { title: 'Recorded', width: 130, render: (_, r) => r.createdAt ? dayjs(r.createdAt).format('DD MMM YYYY') : '—' },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 180,
                          align: 'center',
                          render: (_, record) => (
                            <Space size={6}>
                              <Button
                                shape="circle"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => { setCheckupEditingRecord(record); setCheckupModalOpen(true); }}
                                style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                              />
                              <Button
                                shape="circle"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0', padding: '0 6px' }}
                                onClick={() => {
                                  Modal.confirm({
                                    title: 'Delete Checkup',
                                    content: 'Are you sure you want to delete this checkup record?',
                                    okText: 'Delete',
                                    cancelText: 'Cancel',
                                    onOk: async () => {
                                      try {
                                        await checkupApi.delete(record.id || record._id);
                                        invalidate();
                                        message.success('Checkup deleted');
                                      } catch (err) {
                                        message.error(err.message);
                                      }
                                    },
                                  });
                                }}
                              />
                            </Space>
                          ),
                        },
                      ]}
                      rowKey={(r) => r.id || r._id}
                      pagination={false}
                      locale={{ emptyText: (
                        <div style={{ padding: 40, textAlign: 'center' }}>
                          <HeartOutlined style={{ fontSize: 32, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
                          <div style={{ color: '#666', fontSize: 14 }}>No checkup recorded yet.</div>
                          <div style={{ color: '#999', fontSize: 12 }}>Click Record Checkup to create one.</div>
                        </div>
                      ) }}
                    />
                  </Card>
                )}

                {/* ── Treatment tab ── */}
                {activeTab === 'treatment' && (
                  !isEdit ? <RequireCheckupFirst /> : (
                    <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Treatment Required</Text>
                            <div style={{ marginTop: 4, fontSize: 16, fontWeight: 600 }}>
                              {existingCheckup?.treatmentRequired ? (
                                <Tag color="orange">Yes</Tag>
                              ) : (
                                <Tag>No</Tag>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Treatment Type</Text>
                            <div style={{ marginTop: 4, fontSize: 15, fontWeight: 500 }}>
                              {existingCheckup?.treatmentType || '—'}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                        <Col xs={24} sm={12}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Next Visit (months)</Text>
                            <div style={{ marginTop: 4, fontSize: 15, fontWeight: 500 }}>
                              {existingCheckup?.nextVisitMonths ? `${existingCheckup.nextVisitMonths} months` : '—'}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                        <Col xs={24} sm={12}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Treatment Notes</Text>
                            <div style={{ marginTop: 4, fontSize: 14, color: '#666', whiteSpace: 'pre-wrap' }}>
                              {existingCheckup?.treatmentNotes || '—'}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Doctor Remarks</Text>
                            <div style={{ marginTop: 4, fontSize: 14, color: '#666', whiteSpace: 'pre-wrap' }}>
                              {existingCheckup?.doctorRemarks || '—'}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {isDoctor && (
                        <Button
                          type="link"
                          style={{ marginTop: 16, color: 'var(--wl-primary)' }}
                          onClick={() => { setCheckupEditingRecord(existingCheckup); setCheckupModalOpen(true); }}
                        >
                          Edit Treatment Details
                        </Button>
                      )}
                    </Card>
                  )
                )}

                {/* ── Smile Passport tab ── */}
                {activeTab === 'passport' && (
                  !isEdit ? <RequireCheckupFirst /> : (
                    <>
                      <SmilePassportTab
                        child={child}
                        checkup={existingCheckup}
                        isDoctor={isDoctor}
                        onIssue={(values, done) => issuePassportMutation.mutate(values, { onSuccess: done })}
                        onUpdatePassport={(values, done) => issuePassportMutation.mutate(values, { onSuccess: done })}
                        onRevoke={() => {/* revocation handled elsewhere */}}
                        issueLoading={issuePassportMutation.isPending}
                        revokeLoading={false}
                      />

                      <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)', marginTop: 12 }} bodyStyle={{ padding: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16 }}>
                          <div>
                            <Text strong style={{ fontSize: 15 }}>Smile Passport Records</Text>
                            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                              Manage this child’s Smile Passport record.
                            </Text>
                          </div>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => { setPassportEditingRecord(null); setPassportModalOpen(true); }}
                            disabled={!childId}
                            style={{ borderRadius: 9999, background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
                          >
                            Add Record
                          </Button>
                        </div>
                        <Table
                          dataSource={passportRecords}
                          columns={[
                            { title: '#', width: 60, render: (_, __, i) => i + 1 },
                            { title: 'Academic Year', dataIndex: 'academic_year', width: 140 },
                            { title: 'Created Date', width: 140, render: (_, r) => r.createdAt ? dayjs(r.createdAt).format('DD MMM YYYY') : '—' },
                            { title: 'Concern', dataIndex: 'concern' },
                            {
                              title: 'Status',
                              width: 110,
                              render: (_, r) => (
                                <Tag color={r.status === 'published' ? 'success' : 'default'} style={{ textTransform: 'capitalize' }}>
                                  {r.status || 'draft'}
                                </Tag>
                              ),
                            },
                            {
                              title: 'Actions', key: 'actions', width: 180, align: 'center',
                              render: (_, record) => (
                                <Space size={6}>
                                  <Tooltip title="Preview PDF">
                                    <Button shape="circle" size="small" icon={<FilePdfOutlined />} style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }} onClick={() => { setPassportPreviewRecord(record); setPassportPreviewOpen(true); }} />
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <Button shape="circle" size="small" icon={<EditOutlined />} style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }} onClick={() => { setPassportEditingRecord(record); setPassportModalOpen(true); }} />
                                  </Tooltip>
                                </Space>
                              ),
                            },
                          ]}
                          rowKey={(r) => r.id || r._id}
                          loading={passportLoading}
                          pagination={false}
                          locale={{ emptyText: (
                            <div style={{ padding: 40, textAlign: 'center' }}>
                              <SmileOutlined style={{ fontSize: 32, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
                              <div style={{ color: '#666', fontSize: 14 }}>No Smile Passport records yet.</div>
                              <div style={{ color: '#999', fontSize: 12 }}>Click Add Record to create one.</div>
                            </div>
                          ) }}
                        />
                      </Card>
                    </>
                  )
                )}

              </div>
            </Card>

          </Space>
        )}
      </Col>
      </Row>
      <CheckupModal
        open={checkupModalOpen}
        onClose={() => { setCheckupModalOpen(false); setCheckupEditingRecord(null); }}
        eventId={eventId}
        childId={childId}
        childName={child?.name}
        editingRecord={checkupEditingRecord}
        onSuccess={() => {
          queryClient.invalidateQueries(['event-checkups', eventId]);
          setCheckupModalOpen(false);
          setCheckupEditingRecord(null);
        }}
      />
      <SmilepassportModal
        open={passportModalOpen}
        onClose={() => { setPassportModalOpen(false); setPassportEditingRecord(null); }}
        eventId={eventId}
        childId={childId}
        childName={child?.name}
        editingRecord={passportEditingRecord}
        onSuccess={() => {
          queryClient.invalidateQueries(['event-smilepassports', eventId, childId]);
          setPassportModalOpen(false);
          setPassportEditingRecord(null);
        }}
      />
      <SmilepassportPreviewModal
        open={passportPreviewOpen}
        onClose={() => { setPassportPreviewOpen(false); setPassportPreviewRecord(null); }}
        record={passportPreviewRecord}
        child={child}
      />
    </div>
  );
};

export default CheckupPage;
